import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

export interface ConsultationOnboardingTask {
  id: string;
  label: string;
  status: string;
}

export interface ConsultationShippingAddress {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  postcode: string;
}

export interface ConsultationConsentSnapshot {
  terms: 'granted' | 'declined' | 'revoked';
  privacy: 'granted' | 'declined' | 'revoked';
  healthDataProcessing: 'granted' | 'declined' | 'revoked';
  geneticsContext: 'granted' | 'declined' | 'revoked';
  /** Consent §4.1(4) — retention of raw genetic data, asked separately. */
  geneticDataRetention: 'granted' | 'declined' | 'revoked';
  version: string;
}

export const DEFAULT_CONSULTATION_ONBOARDING_TASKS: ConsultationOnboardingTask[] = [
  { id: 'consent', label: 'Confirm genetics consent and disclaimer', status: 'todo' },
  { id: 'kit', label: 'Send genetics kit signup link', status: 'todo' },
  { id: 'bloods', label: 'Send baseline bloods instructions', status: 'todo' },
  { id: 'consult', label: 'Schedule clinician consult', status: 'todo' },
  { id: 'plan', label: 'Draft and send surveillance plan', status: 'todo' },
];

export async function createConsultationRequest(input: {
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  phone?: string;
  message?: string;
  userId?: string | null;
  packageId: string;
  packageName: string;
  shippingAddress: ConsultationShippingAddress;
  consentSnapshot: ConsultationConsentSnapshot;
}) {
  await addDoc(collection(db, 'consultation_requests'), {
    name: input.displayName,
    displayName: input.displayName,
    firstName: input.firstName,
    lastName: input.lastName,
    email: input.email,
    phone: input.phone || '',
    message: input.message || '',
    shippingAddress: input.shippingAddress,
    userId: input.userId || null,
    packageId: input.packageId,
    packageName: input.packageName,
    source: 'book_page_payment',
    serviceLine: 'melio_therapeutics',
    intakeMode: 'snp_consult_bloods',
    workflowStage: 'new',
    planStatus: 'not_started',
    geneticsConsentAccepted: input.consentSnapshot.geneticsContext === 'granted',
    geneticsConsentVersion: input.consentSnapshot.version,
    privacyConsentAccepted: input.consentSnapshot.privacy === 'granted',
    healthDataProcessingAccepted: input.consentSnapshot.healthDataProcessing === 'granted',
    // §4.1(4). `researchUseStatus` and `commercialDataUseStatus` were written here
    // until Melio stopped seeking either — §4.4 does not permit a purpose outside
    // §4.1's six. Existing consultation_requests keep those fields; nothing new
    // sets them, and the ops projection still reads them for historical records.
    geneticDataRetentionStatus: input.consentSnapshot.geneticDataRetention,
    onboardingTasks: DEFAULT_CONSULTATION_ONBOARDING_TASKS,
    queueLabel: 'genetics_launch',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}
